import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { sendOfferConfirmationEmail } from "@/api/offerConfirmation";
import { toast } from "sonner";

interface ConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId?: number;
  settings: {
    isTestSubmission: boolean;
    isWelcomeEmail: boolean;
    isSendWelcomeEmailDraft: boolean;
    isAttachJointMemberForm: boolean;
    isWithoutII: boolean;
    isWithoutIIGuaranteedReservation: boolean;
  };
  onSettingsChange: (type: 'isTestSubmission' | 'isWelcomeEmail' | 'isSendWelcomeEmailDraft' | 'isAttachJointMemberForm' | 'isWithoutII' | 'isWithoutIIGuaranteedReservation') => void;
  onSave: () => void;
  onCreateAgreement?: () => void;
  isGenerating?: boolean;
  hasFormOfSurrender?: boolean;
}

export function ConfigurationModal({
  isOpen,
  onClose,
  contractId,
  settings,
  onSettingsChange,
  onSave,
  onCreateAgreement,
  isGenerating = false,
  hasFormOfSurrender = false
}: ConfigurationModalProps) {
  const handleSendConfirmationEmail = async () => {
    if (!contractId) {
      toast.error("Contract ID is missing");
      return;
    }

    try {
      const result = await sendOfferConfirmationEmail(contractId);
      if (result.success) {
        toast.success("Confirmation email sent successfully");
      } else {
        toast.error(result.message || "Failed to send confirmation email");
      }
    } catch (error: any) {
      toast.error(error.message || "Error sending confirmation email");
    }
  };

  const handleCreateAgreementClick = async () => {
    // Send confirmation email if checkbox is checked
    if (settings.isWelcomeEmail) {
      await handleSendConfirmationEmail();
    }
    // Then create the agreement
    onCreateAgreement?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            Agreement Configuration
          </DialogTitle>
          <DialogDescription>
            Please review and confirm these settings before generating the agreement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="test"
              checked={settings.isTestSubmission}
              onCheckedChange={() => onSettingsChange('isTestSubmission')}
              disabled={isGenerating}
            />
            <Label htmlFor="test" className="flex flex-col">
              <span>Test Submission</span>
              <span className="text-xs text-muted-foreground">Create Agreement in Test Adobe</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirmationEmail"
              checked={settings.isWelcomeEmail}
              onCheckedChange={() => onSettingsChange('isWelcomeEmail')}
              disabled={isGenerating}
            />
            <Label htmlFor="confirmationEmail" className="flex flex-col">
              <span>Send Confirmation Email</span>
              <span className="text-xs text-muted-foreground">Send confirmation email to the client</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="withoutII"
              checked={hasFormOfSurrender ? false : settings.isWithoutII}
              onCheckedChange={() => onSettingsChange('isWithoutII')}
              disabled={isGenerating || hasFormOfSurrender}
            />
            <Label htmlFor="withoutII" className="flex flex-col">
              <span>Without II</span>
              <span className="text-xs text-muted-foreground">
                {hasFormOfSurrender
                  ? "Not available - this contract has form of surrender entries"
                  : "Create agreement without II"}
              </span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="withoutIIGuaranteedReservation"
              checked={hasFormOfSurrender ? false : settings.isWithoutIIGuaranteedReservation}
              onCheckedChange={() => onSettingsChange('isWithoutIIGuaranteedReservation')}
              disabled={isGenerating || hasFormOfSurrender}
            />
            <Label htmlFor="withoutIIGuaranteedReservation" className="flex flex-col">
              <span>Without II Guaranteed Reservation</span>
              <span className="text-xs text-muted-foreground">
                {hasFormOfSurrender
                  ? "Not available - this contract has form of surrender entries"
                  : "Create agreement without II with guaranteed reservation"}
              </span>
            </Label>
          </div>
        </div>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="sm:mr-auto" disabled={isGenerating}>Cancel</Button>
          <Button
            onClick={onSave}
            variant="outline"
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
            disabled={isGenerating}
          >
            Save Settings
          </Button>
          {onCreateAgreement && (
            <Button
              onClick={handleCreateAgreementClick}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Create Agreement'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}